import { vec2 } from 'gl-matrix';

import { Graphic } from '@canvas/graphics';
import { GraphicBuilder } from '@canvas/graphics/graphic-builder';
import { type Glyph } from '@canvas/icons/glyphs';
import { PathCap } from '@canvas/model/elements/path';

import { messages, type Message } from './i18n';

/**
 * Definition for an arrowhead type that can be applied to path ends
 */
export interface ArrowheadDefinition {
  /**
   * Unique identifier for this arrowhead type. Typically useful for
   * mappings/i18n/etc.
   */
  key: string;

  /**
   * To avoid needing mappings for deriving the PathCap value for each arrowhead,
   * arrowhead, we just attach it to the definition.
   */
  pathCap: PathCap;

  /**
   * Human readable name for the arrowhead. Expected
   */
  tooltipMessage: Message;

  /**
   * The graphic definition for rendering the arrowhead.
   * Arrowhead should be drawn pointing to the right (positive X direction)
   * at origin (0,0) with a natural size for scaling.
   */
  graphic: Graphic;

  /**
   * The natural size of the arrowhead in world coordinates.
   * Used for scaling calculations.
   */
  naturalSize: vec2;

  /**
   * How far back from the path end the arrowhead should be positioned.
   * This prevents overlap between the path line and the arrowhead.
   */
  offsetFromEnd: number;

  /**
   * Fallback icon glpyh - this is a temporary measure while all the icons get
   * moved to AtlasKit.
   */
  glyph: Glyph;

  /**
   * Whether the path segment running underneath the arrowhead should be visible.
   * Some arrowheads can keep their line segment visible to utilize the path's given path presentation.
   */
  pathSegmentVisible: boolean;
}

/**
 * No arrowhead - plain path end
 */
export const noArrowhead = {
  key: 'none',
  pathCap: PathCap.NONE,
  tooltipMessage: messages['whiteboards.arrowhead.none'],
  graphic: new Graphic({
    naturalSize: vec2.fromValues(1, 1),
    content: new GraphicBuilder().build(),
  }),
  naturalSize: vec2.fromValues(0, 0),
  offsetFromEnd: 0,
  pathSegmentVisible: true,
  glyph: 'icon-straight-path',
} as const satisfies ArrowheadDefinition;

/**
 * Basic arrowhead, looks like ->
 */
export const arrowArrowhead = {
  key: 'arrow',
  pathCap: PathCap.ARROW,
  tooltipMessage: messages['whiteboards.arrowhead.arrow'],
  graphic: new Graphic({
    naturalSize: vec2.fromValues(10, 14),
    content: new GraphicBuilder()
      .moveTo([-0.5, 0.5])
      .lineTo([0.5, 0])
      .lineTo([-0.5, -0.5])
      .stroke()
      .build(),
  }),
  naturalSize: vec2.fromValues(10, 14),
  offsetFromEnd: 0,
  pathSegmentVisible: true,
  glyph: 'cap-arrow',
} as const satisfies ArrowheadDefinition;

/**
 * Filled arrow arrowhead
 */
export const filledArrowArrowhead = {
  key: 'filled-arrow',
  pathCap: PathCap.FILLED_ARROW,
  tooltipMessage: messages['whiteboards.arrowhead.filled-arrow'],
  graphic: new Graphic({
    naturalSize: vec2.fromValues(8, 12),
    content: new GraphicBuilder()
      .moveTo([0, 0.5])
      .lineTo([1, 0])
      .lineTo([0, -0.5])
      .close()
      .fill()
      .stroke()
      .build(),
  }),
  naturalSize: vec2.fromValues(8, 12),
  offsetFromEnd: 4,
  pathSegmentVisible: false,
  glyph: 'cap-filled-arrow',
} as const satisfies ArrowheadDefinition;

/**
 * Open arrow arrowhead (no fill, just outline)
 */
export const openArrowArrowhead = {
  key: 'open-arrow',
  pathCap: PathCap.OPEN_ARROW,
  tooltipMessage: messages['whiteboards.arrowhead.open-arrow'],
  graphic: new Graphic({
    naturalSize: vec2.fromValues(11, 15),
    content: new GraphicBuilder()
      .moveTo([0, 0.5])
      .lineTo([1, 0])
      .lineTo([0, -0.5])
      .close()
      .stroke()
      .build(),
  }),
  naturalSize: vec2.fromValues(11, 15),
  offsetFromEnd: 5,
  pathSegmentVisible: false,
  glyph: 'cap-open-arrow',
} as const satisfies ArrowheadDefinition;

/**
 * Filled diamond arrowhead
 */
export const filledDiamondArrowhead = {
  key: 'filled-diamond',
  pathCap: PathCap.FILLED_DIAMOND,
  tooltipMessage: messages['whiteboards.arrowhead.filled-diamond'],
  graphic: new Graphic({
    naturalSize: vec2.fromValues(16, 12),
    content: new GraphicBuilder()
      .moveTo([0, 0])
      .lineTo([0.5, -0.5])
      .lineTo([1, 0])
      .lineTo([0.5, 0.5])
      .close()
      .fill()
      .stroke()
      .build(),
  }),
  naturalSize: vec2.fromValues(16, 12),
  offsetFromEnd: 8,
  pathSegmentVisible: false,
  glyph: 'cap-filled-diamond',
} as const satisfies ArrowheadDefinition;

/**
 * Open diamond arrowhead (outline only)
 */
export const openDiamondArrowhead = {
  key: 'open-diamond',
  pathCap: PathCap.OPEN_DIAMOND,
  tooltipMessage: messages['whiteboards.arrowhead.open-diamond'],
  graphic: new Graphic({
    naturalSize: vec2.fromValues(17, 13),
    content: new GraphicBuilder()
      .moveTo([0, 0])
      .lineTo([0.5, -0.5])
      .lineTo([1, 0])
      .lineTo([0.5, 0.5])
      .close()
      .stroke()
      .build(),
  }),
  naturalSize: vec2.fromValues(17, 13),
  offsetFromEnd: 8,
  pathSegmentVisible: false,
  glyph: 'cap-open-diamond',
} as const satisfies ArrowheadDefinition;

/**
 * Open circle arrowhead (outline only)
 */
export const openCircleArrowhead = {
  key: 'open-circle',
  pathCap: PathCap.OPEN_CIRCLE,
  tooltipMessage: messages['whiteboards.arrowhead.open-circle'],
  graphic: new Graphic({
    naturalSize: vec2.fromValues(9, 9),
    content: new GraphicBuilder()
      .moveTo([1, 0])
      .cubicBezierTo([1, 0.276], [0.776, 0.5], [0.5, 0.5])
      .cubicBezierTo([0.224, 0.5], [0, 0.276], [0, 0])
      .cubicBezierTo([0, -0.276], [0.224, -0.5], [0.5, -0.5])
      .cubicBezierTo([0.776, -0.5], [1, -0.276], [1, 0])
      .close()
      .stroke()
      .build(),
  }),
  naturalSize: vec2.fromValues(9, 9),
  offsetFromEnd: 4,
  pathSegmentVisible: false,
  glyph: 'cap-open-circle',
} as const satisfies ArrowheadDefinition;

/**
 * Perpendicular line / Slash arrowhead
 */
export const slashArrowhead = {
  key: 'slash',
  pathCap: PathCap.SLASH,
  tooltipMessage: messages['whiteboards.arrowhead.slash'],
  graphic: new Graphic({
    naturalSize: vec2.fromValues(8, 12),
    content: new GraphicBuilder().moveTo([0, 0.5]).lineTo([1, -0.5]).stroke().build(),
  }),
  naturalSize: vec2.fromValues(8, 12),
  offsetFromEnd: 4,
  pathSegmentVisible: true,
  glyph: 'cap-slash',
} as const satisfies ArrowheadDefinition;

/**
 * Triple bar arrowhead
 */
export const tripleBarArrowhead = {
  key: 'triple-bar',
  pathCap: PathCap.TRIPLE_BAR,
  tooltipMessage: messages['whiteboards.arrowhead.triple-bar'],
  graphic: new Graphic({
    naturalSize: vec2.fromValues(14, 14),
    content: new GraphicBuilder()
      .moveTo([0.0, -0.5])
      .lineTo([0.0, 0.5])
      .moveTo([0.5, -0.5])
      .lineTo([0.5, 0.5])
      .moveTo([1.0, -0.5])
      .lineTo([1.0, 0.5])
      .moveTo([0, 0])
      .lineTo([1, 0])
      .stroke()
      .build(),
  }),
  naturalSize: vec2.fromValues(14, 14),
  offsetFromEnd: 6,
  pathSegmentVisible: false,
  glyph: 'cap-triple-bar',
} as const satisfies ArrowheadDefinition;

/**
 * Open circle with cross arrowhead
 */
export const openCircleCrossArrowhead = {
  key: 'open-circle-cross',
  pathCap: PathCap.OPEN_CIRCLE_CROSS,
  tooltipMessage: messages['whiteboards.arrowhead.open-circle-cross'],
  graphic: new Graphic({
    naturalSize: vec2.fromValues(14, 14),
    content: new GraphicBuilder()
      // Draw the circle on the left
      .moveTo([0.619, 0])
      .cubicBezierTo([0.619, 0.237], [0.427, 0.429], [0.19, 0.429])
      .cubicBezierTo([-0.047, 0.429], [-0.239, 0.237], [-0.239, 0])
      .cubicBezierTo([-0.239, -0.237], [-0.047, -0.429], [0.19, -0.429])
      .cubicBezierTo([0.427, -0.429], [0.619, -0.237], [0.619, 0])
      .close()
      // Draw the vertical line on the right
      .moveTo([1.0, -0.5])
      .lineTo([1.0, 0.5])
      // Draw the horizontal line on the right
      .moveTo([0.619, 0])
      .lineTo([1.275, 0])
      .stroke()
      .build(),
  }),
  naturalSize: vec2.fromValues(14, 14),
  offsetFromEnd: 10,
  pathSegmentVisible: false,
  glyph: 'cap-open-circle-cross',
} as const satisfies ArrowheadDefinition;

/**
 * Cross arrowhead
 */
export const crossArrowhead = {
  key: 'cross',
  pathCap: PathCap.CROSS,
  tooltipMessage: messages['whiteboards.arrowhead.cross'],
  graphic: new Graphic({
    naturalSize: vec2.fromValues(12, 12),
    content: new GraphicBuilder().moveTo([0.5, -0.5]).lineTo([0.5, 0.5]).stroke().build(),
  }),
  naturalSize: vec2.fromValues(12, 12),
  offsetFromEnd: 6,
  pathSegmentVisible: true,
  glyph: 'cap-cross',
} as const satisfies ArrowheadDefinition;

/**
 * Cross with Crows Foot arrowhead
 */
export const crossWithCrowsFootArrowhead = {
  key: 'cross-with-crows-foot',
  pathCap: PathCap.CROSS_CROWS_FOOT,
  tooltipMessage: messages['whiteboards.arrowhead.cross-with-crows-foot'],
  graphic: new Graphic({
    naturalSize: vec2.fromValues(12, 12),
    content: new GraphicBuilder()
      // Vertical line (cross) first
      .moveTo([0, -0.5])
      .lineTo([0, 0.5])
      // Crows foot after
      .moveTo([0, 0])
      .lineTo([1, -0.5])
      .moveTo([0, 0])
      .lineTo([1, 0])
      .moveTo([0, 0])
      .lineTo([1, 0.5])
      .stroke()
      .build(),
  }),
  naturalSize: vec2.fromValues(12, 12),
  offsetFromEnd: 6,
  pathSegmentVisible: false,
  glyph: 'cap-cross-with-crows-foot',
} as const satisfies ArrowheadDefinition;

/**
 * Crow's foot / Many arrowhead (for ERD diagrams)
 */
export const crowsFootArrowhead = {
  key: 'crows-foot',
  pathCap: PathCap.CROWS_FOOT,
  tooltipMessage: messages['whiteboards.arrowhead.crows-foot'],
  graphic: new Graphic({
    naturalSize: vec2.fromValues(12, 12),
    content: new GraphicBuilder()
      .moveTo([0, 0])
      .lineTo([1, -0.5])
      .moveTo([0, 0])
      .lineTo([1, 0])
      .moveTo([0, 0])
      .lineTo([1, 0.5])
      .stroke()
      .build(),
  }),
  naturalSize: vec2.fromValues(12, 12),
  offsetFromEnd: 6,
  pathSegmentVisible: false,
  glyph: 'cap-crows-foot',
} as const satisfies ArrowheadDefinition;

/**
 * Circle with crow's foot arrowhead (for ERD diagrams)
 */
export const circleCrowsFootArrowhead = {
  key: 'circle-crows-foot',
  pathCap: PathCap.CIRCLE_CROWS_FOOT,
  tooltipMessage: messages['whiteboards.arrowhead.circle-crows-foot'],
  graphic: new Graphic({
    naturalSize: vec2.fromValues(24, 12),
    content: new GraphicBuilder()
      .moveTo([0.5, 0])
      .cubicBezierTo([0.5, 0.276], [0.384, 0.5], [0.25, 0.5])
      .cubicBezierTo([0.116, 0.5], [0, 0.276], [0, 0])
      .cubicBezierTo([0, -0.276], [0.116, -0.5], [0.25, -0.5])
      .cubicBezierTo([0.384, -0.5], [0.5, -0.276], [0.5, 0])
      .close()
      .stroke()
      .moveTo([0.5, 0])
      .lineTo([1, -0.5])
      .moveTo([0.5, 0])
      .lineTo([1, 0])
      .moveTo([0.5, 0])
      .lineTo([1, 0.5])
      .stroke()
      .build(),
  }),
  naturalSize: vec2.fromValues(24, 12),
  offsetFromEnd: 12,
  pathSegmentVisible: false,
  glyph: 'cap-open-circle-crows-foot',
} as const satisfies ArrowheadDefinition;

/**
 * All available arrowhead definitions indexed by key
 */
export const arrowheadDefinitions = {
  [noArrowhead.key]: noArrowhead,
  [arrowArrowhead.key]: arrowArrowhead,
  [filledArrowArrowhead.key]: filledArrowArrowhead,
  [openArrowArrowhead.key]: openArrowArrowhead,
  [filledDiamondArrowhead.key]: filledDiamondArrowhead,
  [openDiamondArrowhead.key]: openDiamondArrowhead,
  [openCircleArrowhead.key]: openCircleArrowhead,
  [slashArrowhead.key]: slashArrowhead,
  [tripleBarArrowhead.key]: tripleBarArrowhead,
  [openCircleCrossArrowhead.key]: openCircleCrossArrowhead,
  [crossArrowhead.key]: crossArrowhead,
  [crossWithCrowsFootArrowhead.key]: crossWithCrowsFootArrowhead,
  [crowsFootArrowhead.key]: crowsFootArrowhead,
  [circleCrowsFootArrowhead.key]: circleCrowsFootArrowhead,
} as const;

/**
 * Array of all arrowhead definitions for enumeration
 */
export const allArrowheadDefinitions = Object.values(arrowheadDefinitions);

/**
 * Type representing valid arrowhead keys
 */
export type ArrowheadKey = keyof typeof arrowheadDefinitions;

/**
 * Default arrowhead for new paths
 */
export const defaultArrowhead = noArrowhead;

/**
 * Get an arrowhead definition by key, with fallback to default
 */
export function getArrowheadDefinition(key: string): ArrowheadDefinition {
  return arrowheadDefinitions[key as ArrowheadKey] ?? defaultArrowhead;
}

type PathCapValue = (typeof PathCap)[keyof typeof PathCap];

/**
 * Mapping from PathCap enum values to arrowhead definitions
 */
export const pathCapToArrowhead = allArrowheadDefinitions.reduce(
  (acc, arrowhead) => {
    acc[arrowhead.pathCap] = arrowhead;
    return acc;
  },
  {} as Record<PathCapValue, (typeof arrowheadDefinitions)[ArrowheadKey]>,
);

/**
 * Get arrowhead definition from PathCap enum value
 */
export function getArrowheadFromPathCap(pathCap: PathCapValue | undefined) {
  if (pathCap === undefined) {
    return defaultArrowhead;
  }
  return pathCapToArrowhead[pathCap] ?? defaultArrowhead;
}
